import { useState, useEffect } from "react";
import 'leaflet/dist/leaflet.css';

// react-router components
import { Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import CircularProgress from "@mui/material/CircularProgress";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";

// Material Dashboard 2 React example components
import Sidenav from "examples/Sidenav";

// Material Dashboard 2 React themes
import theme from "assets/theme";
import themeDark from "assets/theme-dark";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context";

// API context
import { ApiProvider } from 'context/ApiContext';

// Images
import brandWhite from "assets/images/logo_engiegreen.png";
import brandDark from "assets/images/logo_engiegreen.png";

export default function App() {
    const [controller, dispatch] = useMaterialUIController();
    const {
        miniSidenav,
        direction,
        layout,
        sidenavColor,
        transparentSidenav,
        whiteSidenav,
        darkMode,
    } = controller;

    const [onMouseEnter, setOnMouseEnter] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const { pathname } = useLocation();
    const navigate = useNavigate();

    const token = localStorage.getItem('token');
    const role = localStorage.getItem('userRole');

    const isTokenValid = token !== null && token !== "";
    const isAdmin = role === "AdminAT" || role === "AdminEnterprise";

    // Token check on mount
    useEffect(() => {
        if (!token) {
            navigate("/authentication/sign-in");
        }
        setIsLoading(false);
    }, [navigate, token]);

    // Mouse handlers for sidenav
    const handleOnMouseEnter = () => {
        if (miniSidenav && !onMouseEnter) {
            setMiniSidenav(dispatch, false);
            setOnMouseEnter(true);
        }
    };

    const handleOnMouseLeave = () => {
        if (onMouseEnter) {
            setMiniSidenav(dispatch, true);
            setOnMouseEnter(false);
        }
    };

    // Direction and scroll effects
    useEffect(() => {
        document.body.setAttribute("dir", direction);
    }, [direction]);

    useEffect(() => {
        document.documentElement.scrollTop = 0;
        document.scrollingElement.scrollTop = 0;
    }, [pathname]);

    // Route filtering based on role
    const getRoutes = (allRoutes) =>
        allRoutes.flatMap((route) => {
            if (route.collapse) {
                return getRoutes(route.collapse);
            }

            if (route.route === "/administration") {
                return isTokenValid && isAdmin
                    ? <Route exact path={route.route} element={route.component} key={route.key} />
                    : [];
            }

            if (route.route) {
                return <Route exact path={route.route} element={route.component} key={route.key} />;
            }

            return [];
        });

    // Filter routes for sidenav
    const filteredRoutes = routes.filter((route) => {
        if (route.route === "/administration") {
            return isTokenValid && isAdmin;
        }
        return true;
    });

    const isAuthPage = pathname.startsWith("/authentication");
    const isProfilePage = pathname.startsWith("/profile");

    return (
        <ApiProvider>
            <ThemeProvider theme={darkMode ? themeDark : theme}>
                <CssBaseline />
                {isLoading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center" height="100vh">
                        <CircularProgress color="primary" />
                    </MDBox>
                ) : (
                    <>
                        {!isAuthPage && !isProfilePage && layout === "turbineDashboard" && (
                            <Sidenav
                                color={sidenavColor}
                                brand={
                                    (transparentSidenav && !darkMode) || whiteSidenav
                                        ? brandDark
                                        : brandWhite
                                }
                                routes={filteredRoutes}
                                onMouseEnter={handleOnMouseEnter}
                                onMouseLeave={handleOnMouseLeave}
                            />
                        )}
                        <Routes>
                            {getRoutes(routes)}
                            <Route
                                path="*"
                                element={
                                    <Navigate to={isTokenValid ? "/parkList" : "/authentication/sign-in"} />
                                }
                            />
                        </Routes>
                    </>
                )}
            </ThemeProvider>
        </ApiProvider>
    );
}
